import { useMemo, useState } from "react";
import { FiRefreshCw, FiUsers, FiMonitor } from "react-icons/fi";
import { IoIosClose } from "react-icons/io";
import { MdOutlineStorefront } from "react-icons/md";
import Header from "../../components/header/index.jsx";
import { Loading } from "../../components/loading";
import usePolling from "../../hooks/usePolling.js";
import useWindowSize from "../../hooks/useWindowSize.js";
import CompanyCard from "./components/companyCard/index.jsx";
import ConnectedUsersModal from "./components/connectedUsersModal/index.jsx";
import { getOpenCompanies } from "./services/getOpenCompanies.js";
import { formatTime } from "./utils.js";
import styles from "./css/openCompanies.module.css";

// A LISTA É ATUALIZADA A CADA 30 s, SÓ ENQUANTO ESTA TELA ESTÁ ABERTA (O HOOK CANCELA TUDO AO DESMONTAR)
const POLLING_INTERVAL_MS = 30000;

// ABAIXO DISSO O Header DO ADMIN FICA FIXO E COM POUCA LARGURA (RESERVA ESPAÇO PARA O BOTÃO DO MENU)
const MOBILE_BREAKPOINT = 768;


function SummaryCard({ icon: Icon, label, value, color }) {
    return (
        <div className={styles.summaryCard}>
            <div className={styles.summaryIcon} style={{ backgroundColor: `${color}1f`, color: color }}>
                <Icon size={22} />
            </div>

            <div className={styles.summaryTexts}>
                <span className={styles.summaryValue}>{value}</span>
                <span className={styles.summaryLabel}>{label}</span>
            </div>
        </div>
    );
}


export default function OpenCompanies() {
    const { data, error, loading, refreshing, lastUpdatedAt, refresh } = usePolling(getOpenCompanies, { intervalMs: POLLING_INTERVAL_MS });

    const width = useWindowSize();
    const isMobile = width <= MOBILE_BREAKPOINT;

    // BUSCA
    const [search, setSearch] = useState('');

    // EMPRESA COM O MODAL DE USUÁRIOS ABERTO (GUARDA SÓ O ID: OS DADOS DA LINHA VÊM SEMPRE DA LISTA MAIS RECENTE)
    const [selectedCompanyId, setSelectedCompanyId] = useState(null);
    const [selectedSnapshot, setSelectedSnapshot] = useState(null);


    const companies = data?.companies;

    // FILTRO APLICADO NA RENDERIZAÇÃO, SOBRE A LISTA MAIS RECENTE (A BUSCA NUNCA FICA DESATUALIZADA)
    const visibleCompanies = useMemo(() => {
        const term = search.trim().toLowerCase();

        const list = (companies ?? []).filter((item) =>
            !term ||
            item.name?.toLowerCase().includes(term) ||
            item.document?.toLowerCase().includes(term)
        );

        // MAIS USUÁRIOS CONECTADOS PRIMEIRO, DEPOIS O TURNO MAIS ANTIGO
        return [...list].sort((a, b) =>
            (b.connectedUsers - a.connectedUsers) ||
            (new Date(a.openedAt).getTime() - new Date(b.openedAt).getTime())
        );
    }, [companies, search]);

    const totals = useMemo(() => {
        const list = companies ?? [];

        return {
            open: list.length,
            users: list.reduce((sum, item) => sum + item.connectedUsers, 0),
            localApp: list.filter((item) => item.localAppConnected).length,
        };
    }, [companies]);


    // LINHA ATUAL DA EMPRESA SELECIONADA; SE SUMIU DA LISTA, O TURNO FOI ENCERRADO E USA-SE O ÚLTIMO RETRATO CONHECIDO
    const selectedCurrent = companies?.find((item) => item.companyId === selectedCompanyId) ?? null;
    const selectedCompany = selectedCurrent ?? selectedSnapshot;


    const openUsersModal = (company) => {
        setSelectedSnapshot(company);
        setSelectedCompanyId(company.companyId);
    };

    const closeUsersModal = () => {
        setSelectedCompanyId(null);
        setSelectedSnapshot(null);
    };


    // CAIXA DE BUSCA (RENDERIZADA NO HEADER NO DESKTOP E NO CONTEÚDO NO MOBILE)
    const searchBox = (
        <div className={styles.searchBar}>
            <input
                className={styles.inputSearchStyle}
                placeholder="Buscar por nome ou CNPJ..."
                onChange={({ target: { value } }) => setSearch(value)}
                value={search}
            />

            <button
                className={styles.clearSearchButton}
                onClick={() => setSearch('')}
            >
                <IoIosClose size={22} color={search.length > 0 ? 'red' : 'grey'} />
            </button>
        </div>
    );


    // SCREEN ====================================>
    return (
        <div>
            <Header>
                <h2 className={styles.headerTitle}>Restaurantes abertos</h2>

                <div className={styles.headerActions}>
                    {/* NO MOBILE A BUSCA SAI DO HEADER (NÃO CABE) E VAI PARA O INÍCIO DO CONTEÚDO */}
                    {!isMobile && searchBox}

                    <button
                        type="button"
                        className={styles.refreshButton}
                        onClick={refresh}
                        disabled={loading || refreshing}
                        title="Atualizar agora"
                    >
                        <FiRefreshCw size={18} className={refreshing ? styles.spinningIcon : undefined} />
                        <span className={styles.refreshText}>Atualizar</span>
                    </button>
                </div>
            </Header>


            <div className={styles.container}>
                <div className={styles.contentArea}>

                    {isMobile && <div className={styles.mobileSearch}>{searchBox}</div>}

                    {loading ? (
                        <Loading />

                    ) : !data ? (
                        <div className={styles.errorBox}>
                            <p>Não foi possível carregar os restaurantes abertos no momento.</p>
                            <button type="button" className={styles.retryButton} onClick={refresh}>Tentar novamente</button>
                        </div>

                    ) : (
                        <>
                            <div className={styles.summaryRow}>
                                <SummaryCard icon={MdOutlineStorefront} label="Restaurantes abertos" value={totals.open} color="#005586" />
                                <SummaryCard icon={FiUsers} label="Usuários conectados" value={totals.users} color="#2e7d32" />
                                <SummaryCard icon={FiMonitor} label="Com app local conectado" value={totals.localApp} color="#0288d1" />
                            </div>

                            <div className={styles.statusBar}>
                                <span>
                                    Atualizado às <b>{formatTime(lastUpdatedAt)}</b> · atualiza a cada 30 s
                                </span>

                                {error && (
                                    <span className={styles.errorNotice}>
                                        Falha ao atualizar. Mostrando os dados de {formatTime(lastUpdatedAt)}.
                                    </span>
                                )}
                            </div>

                            {companies.length === 0 ? (
                                <p className={styles.noDataText}>Nenhum restaurante com turno aberto no momento.</p>

                            ) : visibleCompanies.length === 0 ? (
                                <p className={styles.noDataText}>Nenhum restaurante encontrado para a busca realizada.</p>

                            ) : (
                                <div className={styles.grid}>
                                    {visibleCompanies.map((company) => (
                                        <CompanyCard
                                            key={company.companyId}
                                            company={company}
                                            onOpenUsers={openUsersModal}
                                        />
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>


            <ConnectedUsersModal
                show={!!selectedCompanyId}
                company={selectedCompany}
                stillOpen={!!selectedCurrent}
                onClose={closeUsersModal}
            />
        </div>
    );
}
